import LegalLayout, { H2, P, UL } from "./LegalLayout";

export default function TermsOfService() {
  return (
    <LegalLayout title="Terms of Service" updated="30 Aug 2026">
      <P>
        These Terms govern your use of rsell ai, operated at{" "}
        <a href="https://rsellai.top" className="text-[#10B981] underline">rsellai.top</a> from Lithuania, EU
        (contact: <a href="mailto:rsell.ai.real@gmail.com" className="text-[#10B981] underline">rsell.ai.real@gmail.com</a>).
        By using the service you agree to these Terms.
      </P>

      <H2>1. What rsell ai does</H2>
      <P>
        rsell ai analyses photos of resale items and generates a pricing report based on AI-inferred and public market data (eBay listings and, when available, eBay sold-listing data). Reports are shown as guidance and include estimated prices, sell-through indicators, listing copy suggestions, and shipping estimates.
      </P>

      <H2>2. Estimates are informational, not guarantees</H2>
      <P>
        <strong>All prices, sell-through rates, and market indicators shown by rsell ai are informational only.</strong> Actual selling prices depend on your listing photos, timing, buyer demand, item condition, and platform-specific dynamics. We do not guarantee that any item will sell at, above, or below the shown price, nor do we guarantee a sale at all. You are responsible for your own listings, pricing decisions, and compliance with the terms of any marketplace you use.
      </P>

      <H2>3. Accounts and eligibility</H2>
      <UL>
        <li>You must be at least 18 years old to purchase a paid plan.</li>
        <li>You are responsible for anything that happens under your account.</li>
        <li>We may suspend accounts for abuse, fraud, chargebacks, or breach of these Terms.</li>
      </UL>

      <H2>4. Free tier and paid plans</H2>
      <UL>
        <li>Free tier grants a limited number of scans per device.</li>
        <li>Paid plans (Starter, Pro, Power Seller) are sold by monthly subscription through Whop.</li>
        <li>Credits are attached to your account; unused credits carry over as long as the account is active and in good standing.</li>
        <li>We may adjust tier limits and pricing with 30 days' notice for existing subscribers.</li>
      </UL>

      <H2>5. Payments and billing</H2>
      <P>
        All payments are processed by Whop. By purchasing you also agree to <a href="https://whop.com/legal/terms-of-service" className="text-[#10B981] underline">Whop's Terms of Service</a>. Subscriptions renew automatically until cancelled. You can cancel at any time from your Whop dashboard or by emailing us; cancellation takes effect at the end of your current billing period.
      </P>

      <H2>6. Refunds</H2>
      <P>
        <strong>All sales are final. We do not issue refunds</strong> for partial billing periods, unused credits, or accidental purchases. Because credits and reports are digital goods delivered instantly and consumed on first use, EU distance-selling rules do not require a cooling-off period once you've used the service. This policy is disclosed at checkout before you pay. See our{" "}
        <a href="/refund-policy" className="text-[#10B981] underline">Refund Policy</a> for details.
      </P>

      <H2>7. Acceptable use</H2>
      <UL>
        <li>No automated scraping, mirroring, or resale of rsell ai output.</li>
        <li>No use of the service to identify, price, or list items that are illegal to resell in your jurisdiction.</li>
        <li>No uploading of content you don't own the rights to.</li>
        <li>No reverse engineering, security probing, or interfering with other users.</li>
      </UL>

      <H2>8. Intellectual property</H2>
      <P>
        You keep the rights to the photos you upload. You grant us a limited licence to process them for the purpose of generating your report. The reports, listing copy, and analytics we produce for you are yours to use however you wish, including on marketplaces. All product design, code, and copy on rsellai.top are our property.
      </P>

      <H2>9. Third-party dependencies</H2>
      <P>
        We rely on OpenAI, Anthropic, Google, eBay, Whop, hCaptcha, MongoDB Atlas, and Vultr. If any of these providers has downtime or changes their API, parts of the service may be temporarily unavailable. We aim for high availability but do not guarantee uptime.
      </P>

      <H2>10. Liability</H2>
      <P>
        To the maximum extent permitted by law, our total liability to you for any claim arising from your use of rsell ai is limited to the amount you paid us in the 12 months preceding the claim. We are not liable for lost profits, missed sales, chargebacks, marketplace suspensions, or any indirect or consequential damages. Nothing in these Terms limits liability for death, personal injury caused by our negligence, or fraud.
      </P>

      <H2>11. Termination</H2>
      <P>
        You can stop using the service at any time. We can suspend or terminate access for breach of these Terms, non-payment, or abuse. Sections 2, 6, 8, 10 and 13 survive termination.
      </P>

      <H2>12. Changes to these Terms</H2>
      <P>
        Material changes are announced in-app and by email at least 14 days before they take effect. Continued use after the effective date is acceptance of the new Terms.
      </P>

      <H2>13. Governing law</H2>
      <P>
        These Terms are governed by the laws of the Republic of Lithuania. Any dispute is subject to the exclusive jurisdiction of the courts of Vilnius, Lithuania.
      </P>

      <H2>Legal note</H2>
      <P className="!text-white/45 text-sm">
        This document is a structured template, not personalised legal advice. Have a lawyer review it before you rely on it in a dispute — particularly Section 10.
      </P>
    </LegalLayout>
  );
}
